import React from 'react';
import { DateTime } from 'src_common/utils/time/time';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { CustomerButton } from 'src/components/FraudAndCompliance/CustomerButton/CustomerButton';
import { ResponsibleGamblingTabItemsRoute } from 'src/components/CustomersBar/CustomerTab/ResponsibleGamblingTab/ResponsibleGamblingTabs';
import { getGamstopStatusLabel } from 'src/components/FraudAndCompliance/FraudAndCompliancesTab/FraudAndCompliances/components/gamstopNotificationUtils';
import {
    ColumnHeader,
    SectionHeader,
    SectionMessage,
    SectionWrapper,
    TableCell,
    TableHeaderRow,
    TableRow,
    TableWrapper,
} from './GamstopTab.style';
import {
    ChevronDownIconWrapper,
    Select,
    SelectWrapper,
} from 'src/components/FraudAndCompliance/FraudAndCompliancesTab/FraudAndCompliances/components/CommonTable.styles';
import { GamstopNotificationType } from 'src_server/types/notificationData';
import { Button } from 'src/domains/common/components/button/button';
import { GamstopTabState } from './GamstopTab.state';

const ACTIONS = {
    your: [
        { action: 'cancel', name: 'Mark as reviewed' },
        { action: 'unassign', name: 'Unassign' },
    ],
    none: [{ action: 'assign', name: 'Assign to me' }],
    other: [{ action: 'unassign', name: 'Unassign' }],
} as const;

type ActionsType = keyof typeof ACTIONS;

const GAMSTOP_AUDIT_ROUTE_PARAMS = {
    tab: 'customer',
    'tab.tab': 'responsible-gambling',
    'tab.tab.tab': ResponsibleGamblingTabItemsRoute.GAMSTOP,
    audits: 'gamstopAudit',
} as const;

interface OptionType {
    action: string;
    name: string;
}

const getOption = (option: OptionType, id: string): React.ReactElement => {
    return (
        <option
            key={`${id}-${option.action}`}
            value={option.action}
        >
            {option.name}
        </option>
    );
};

interface GamStopSectionPropsType {
    type: ActionsType;
    header: string;
    dataTest: string;
    notifications: Array<GamstopNotificationType>;
    state: GamstopTabState;
    isFinished: boolean;
}

export const GamStopSection = observer(
    'GamStopSection',
    ({ type, header, dataTest, notifications, state, isFinished }: GamStopSectionPropsType): React.ReactElement => {
        const { models, reduxAction, routerBackoffice } = useAppStateContext();

        const openGamstopAudit = (customerId: number): void => {
            const currentCustomerId = routerBackoffice.getValueString('customers');
            reduxAction.customersActionsOpenRequest(customerId, undefined, GAMSTOP_AUDIT_ROUTE_PARAMS);
            if (currentCustomerId === String(customerId)) {
                routerBackoffice.redirectToNamespace(`customers:${customerId}`, GAMSTOP_AUDIT_ROUTE_PARAMS);
            }
        };

        const doAction = async (
            event: React.ChangeEvent<HTMLSelectElement>,
            notification: GamstopNotificationType
        ): Promise<void> => {
            const action = event.target.value;

            const mutationAction =
                action === 'unassign'
                    ? 'unassign'
                    : action === 'assign'
                      ? 'assign'
                      : action === 'cancel'
                        ? 'close'
                        : null;

            if (mutationAction === null) {
                return;
            }

            try {
                await state.updateNotification(notification, mutationAction);
            } catch (error) {
                console.error(`GamStop ${action} error`, error);
            }
        };

        const areNoItems = isFinished && notifications.length === 0;

        return (
            <SectionWrapper data-test={dataTest}>
                <SectionHeader data-test='section-header'>{header}</SectionHeader>
                {areNoItems ? (
                    <SectionMessage>There are no items</SectionMessage>
                ) : (
                    <TableWrapper>
                        <thead>
                            <TableHeaderRow>
                                <ColumnHeader>Date Detected</ColumnHeader>
                                <ColumnHeader>Customer</ColumnHeader>
                                <ColumnHeader>Gamstop Audit</ColumnHeader>
                                <ColumnHeader>Status</ColumnHeader>
                                <ColumnHeader>Action</ColumnHeader>
                            </TableHeaderRow>
                        </thead>
                        <tbody>
                            {notifications.map((notification) => (
                                <TableRow
                                    key={notification.id}
                                    section={type}
                                    data-test={`gamstop-notification-row-${notification.accountId}`}
                                >
                                    <TableCell data-test='gamstop-date-detected'>
                                        {DateTime.from(notification.createdAt)?.format('DD-MM-YYYY HH:mm') ?? '-'}
                                    </TableCell>
                                    <TableCell data-test='gamstop-customer'>
                                        <CustomerButton
                                            customerId={notification.accountId}
                                            models={models}
                                        />
                                    </TableCell>
                                    <TableCell data-test='gamstop-audit'>
                                        <Button
                                            onClick={(): void => openGamstopAudit(notification.accountId)}
                                            variant='link'
                                            icon='external-link'
                                            size='sm'
                                            dataTest='gamstop-audit-button'
                                        >
                                            Gamstop Audit
                                        </Button>
                                    </TableCell>
                                    <TableCell data-test='gamstop-status'>
                                        {getGamstopStatusLabel(notification)}
                                    </TableCell>
                                    <TableCell disableColor={true}>
                                        <SelectWrapper>
                                            <Select
                                                name='action'
                                                defaultValue=''
                                                onChange={(event): void => {
                                                    void doAction(event, notification);
                                                }}
                                                data-test='gamstop-action-form'
                                            >
                                                <option value=''>Choose an action</option>
                                                {ACTIONS[type].map((option) => getOption(option, notification.id))}
                                            </Select>
                                            <ChevronDownIconWrapper />
                                        </SelectWrapper>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </tbody>
                    </TableWrapper>
                )}
            </SectionWrapper>
        );
    }
);
